import React, {useEffect, useState} from "react"
import {DocumentableAllowedToEvaluateUserOnly} from "../../containers/PermissionedUser/PermissionedUser"
import {useModal} from "react-modal-hook"
import {AddToast} from "react-toast-notifications"
import {RejectApplicationDocumentModal} from "./RejectApplicationDocument"
import {
	ApplicationDocument,
	ApplicationDocumentSide,
	ApplicationDocumentStatus,
	ApplicationDocumentType,
	ApplicationStatus,
	DocumentLink,
	PatchApplicationDocument,
} from "../../resources/application"
import {
	DocumentableApplication,
	documentableApproveDocument,
	documentableGetDocumentLink,
	documentableUpdateDocument,
} from "../../resources/documentable"
import {ApplicationDocumentModalHeader} from "./ApplicationDocumentModalHeader"
import {ImageCard} from "../ImageCard/ImageCard"
import Icon from "../Icon/Icon"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AsyncResult} from "../../types/asyncResult"
import {ErrorDocument, OkDocument} from "../../resources/common"
import EditDocumentForm, {editDocumentFormId} from "./EditDocument/EditDocumentForm"
import {DocumentEditMode} from "./EditMode/DocumentEditMode"
import {DocumentPreviewMode} from "./PreviewMode/DocumentPreviewMode"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import Loading from "../Loading/Loading"
import {useDocumentableIsAllowUpdateDocument} from "../../services/documentablePermission"

interface ModalAsideProps {
	application: DocumentableApplication
	document: ApplicationDocument
	accessToken: string
	refresh: () => void
	close: () => void
	addToast: AddToast
}

export function ApplicationDocumentModalAside(props: ModalAsideProps) {
	const {application, document, accessToken, refresh, close, addToast} = props

	const [isEditMode, setIsEditMode] = useState(false)
	const [updateDocumentState, updateDocumentRequest] = useAsyncResultIdle(documentableUpdateDocument)
	const isDocumentResubmitted = document.attributes.resubmitted ?? false
	const [loading, setLoading] = useState(false)
	const [approveError, setApproveError] = useState<string | null>(null)
	const [isApproving, setIsApproving] = useState(false)
	const isAllowUpdateDocument = useDocumentableIsAllowUpdateDocument(application)

	const canReject =
		application.attributes.status !== ApplicationStatus.Approved &&
		application.attributes.status !== ApplicationStatus.Denied &&
		application.attributes.status !== ApplicationStatus.Pending &&
		document.attributes.status !== ApplicationDocumentStatus.Invalid &&
		!isEditMode

	const canApprove =
		application.attributes.status !== ApplicationStatus.Approved &&
		application.attributes.status !== ApplicationStatus.Denied &&
		application.attributes.status !== ApplicationStatus.Pending &&
		document.attributes.status !== ApplicationDocumentStatus.Approved &&
		!isEditMode

	const [showReject, hideReject] = useModal(() => (
		<RejectApplicationDocumentModal
			close={hideReject}
			application={application}
			documentId={document.id}
			onSuccess={() => {
				hideReject()
				close()
				refresh()
			}}
		/>
	))

	const additionalDocumentLinkState =
		document.attributes.documentType === ApplicationDocumentType.selfieVerification &&
		document.attributes.status !== ApplicationDocumentStatus.Required
			? useAsyncResult(() =>
					documentableGetDocumentLink(application, document.id, accessToken, ApplicationDocumentSide.Additional)
			  )
			: AsyncResult.pending<OkDocument<DocumentLink>, ErrorDocument>()

	const additionalDocumentLink = additionalDocumentLinkState.match(
		() => undefined,
		(image) => image,
		() => null
	)

	const imageUrl = additionalDocumentLink?.data?.attributes?.link
	const selfieImage = imageUrl
		? {
				src: imageUrl,
				alt: "Selfie",
		  }
		: null

	useEffect(() => {
		updateDocumentState.match(
			() => undefined,
			() => undefined,
			() => {
				addToast("Document updated successfully", {appearance: "success"})
				setIsEditMode(false)
				refresh()
				setLoading(false)
			},
			() => {
				addToast("Couldn't update information, please refresh and try again", {appearance: "error"})
				setLoading(false)
			}
		)
	}, [updateDocumentState])

	return (
		<aside className="modal-sidebar">
			<ApplicationDocumentModalHeader id={document.id} status={document.attributes.status} close={close} />
			{selfieImage && (
				<ImageCard
					title={"Selfie"}
					prefixIcon={<Icon size={16} icon={"face-id-user--interface-essential"} />}
					image={selfieImage}
				></ImageCard>
			)}
			{loading && <Loading />}
			{isAllowUpdateDocument && isEditMode ? (
				<DocumentEditMode
					className="edit-mode-container"
					onCancel={() => setIsEditMode(!isEditMode)}
					isDocumentResubmitted={isDocumentResubmitted}
					formId={editDocumentFormId}
				>
					<EditDocumentForm
						document={document}
						updateDocumentRequest={(documentData: PatchApplicationDocument) => {
							setLoading(true)
							updateDocumentRequest(application, accessToken, document.id, documentData)
						}}
					/>
				</DocumentEditMode>
			) : (
				<DocumentPreviewMode
					onEditButtonClick={() => setIsEditMode(!isEditMode)}
					application={application}
					document={document}
				/>
			)}
			{document.attributes.documentType !== ApplicationDocumentType.clientRequested && (
				<DocumentableAllowedToEvaluateUserOnly application={application}>
					<div className="sidebar-buttons">
						{approveError && <div className="approve-error has-text-danger">{approveError}</div>}
						<div className="sidebar-buttons-actions">
							<button className="button is-danger" disabled={!canReject} onClick={showReject}>
								Reject
							</button>
							<button
								className="button is-success"
								disabled={!canApprove || isApproving}
								onClick={() => {
									setApproveError(null)
									setIsApproving(true)
									documentableApproveDocument(application, accessToken, document.id).then((result) => {
										setIsApproving(false)
										if (result.isErr()) {
											setApproveError(result.error.errors?.[0]?.title || "Failed to approve document")
											return
										}
										close()
										refresh()
									})
								}}
							>
								Approve
							</button>
						</div>
					</div>
				</DocumentableAllowedToEvaluateUserOnly>
			)}
		</aside>
	)
}
